import { defineStore } from 'pinia';
import { ESsPlayerFreespinStage } from '~/types/softswiss/ssPlayerBonuses';
import { ETriggerId } from '~/types/toasts';
import { GAME_SORTING_GROUP_MAP, GROUPS_FOR_SOME_GAMES_PROVIDER } from '~/utils/constants';
import { catalogueThumbsCount } from '~/utils/defaultData/catalogueThumbsCount';
import { gameUrlFromTitle } from '~/utils/gameUrl';
import { useStoreRoot } from '../abstract/root';
import { useStoreInGameCurrencyExchange } from '../client/inGameCurrencyExchange';
import { useStoreCurrencyMenu } from '../strapi/currencyMenu';
import { useStoreToasts } from '../strapi/toasts';
import { bitBack } from '../transport/bitBack';
import { useStoreSsAccounts } from './accounts';
import { useStoreSsPlayer } from './player';
import { useStoreSsPlayerBonuses } from './playerBonuses';
import type {
  IGamesForProvidersPage,
  IGamesRequestOptions,
  ISsGame,
  ISsGamesResponse,
} from '~/types/softswiss/ssGames';

export const useStoreSsGames = defineStore('ssGames', {
  state: () => ({
    gamesFromHitsCollection: [] as ISsGame[],
    gamesForActiveTournaments: [] as {
      collection: string;
      tournamentId: string;
      games: ISsGame[];
    }[],
    recentlyPlayedGames: [] as ISsGame[],
  }),
  actions: {
    async loadGames({
      currency,
      search,
      ids,
      favoritgames,
      provider,
      collection,
      offset,
      limit,
      includerestricted,
      includeProvidersCount,
      sortBy,
      order,
    }: IGamesRequestOptions) {
      const urlSearch = new URLSearchParams();

      if (collection) {
        let requestCollection = collection;

        if (collection === 'hits') {
          requestCollection = this.findReplacementCollection() || collection;
        }

        urlSearch.append('collection', requestCollection);
      }

      if (ids) {
        urlSearch.append('ids', ids.join(','));
      }

      if (favoritgames) {
        urlSearch.append('realids', favoritgames.join(','));
      }

      if (limit) {
        urlSearch.append('limit', String(limit));
      }

      if (offset) {
        urlSearch.append('offset', String(offset));
      } else {
        urlSearch.append('offset', '0');
      }

      if (provider) {
        urlSearch.append('providers', provider);
      }

      if (search) {
        urlSearch.append('search', search);
      }

      if (currency) {
        urlSearch.append('currency', currency);
      }

      if (includerestricted) {
        urlSearch.append('ignoreGeo', '1');
      }
      if (includeProvidersCount) {
        urlSearch.append('includeProvidersCount', '1');
      }
      if (sortBy) {
        urlSearch.append('sortBy', sortBy);
      }
      if (order) {
        urlSearch.append('order', order);
      }

      if (usePlatform().isDesktop) {
        urlSearch.append('desktop', '1');
      } else {
        urlSearch.append('mobile', '1');
      }

      const groups = GROUPS_FOR_SOME_GAMES_PROVIDER.filter((groupId) =>
        useStoreSsPlayer().playerGroupIds.includes(groupId)
      );

      if (groups.length) {
        urlSearch.append('playerGroups', groups.join(','));
      }

      console.log('Fetching games with params:', urlSearch.toString());

      const games: ISsGamesResponse = await bitBack().get({
        url: '/backend/games?' + urlSearch.toString(),
        userId: useStoreSsPlayer().playerId,
      });

      return games;
    },
    async loadProviders() {
      const urlSearch = new URLSearchParams();
      let currency = '';

      if (
        useStoreSsPlayer().isPlayerAuthorised &&
        useStoreCurrencyMenu().isFiatCurrency(useStoreRoot().userCurrency)
      ) {
        currency = useStoreRoot().userCurrency;
      }

      urlSearch.append('currency', currency);

      const providers: string[] = await bitBack().get({
        url: '/backend/games/providers?' + urlSearch.toString(),
      });

      return providers;
    },
    async loadGamesForProvidersPage() {
      const games: IGamesForProvidersPage = await bitBack().get({
        url: '/backend/games/providers-aggregated',
      });

      return games;
    },
    async loadGamesFromHitsCollection() {
      const storeRoot = useStoreRoot();
      const params = { collection: 'hits' } as IGamesRequestOptions;

      params.limit = usePlatform().isMobile
        ? catalogueThumbsCount(useStoreRoot().screen.width) * 4
        : catalogueThumbsCount(useStoreRoot().screen.width) * 2;

      if (
        useStoreSsPlayer().isPlayerAuthorised &&
        useStoreCurrencyMenu().isFiatCurrency(storeRoot.userCurrency)
      ) {
        params.currency = storeRoot.userCurrency;
      }

      const games: ISsGamesResponse = await this.loadGames(params);

      this.gamesFromHitsCollection = games.data;
    },
    async loadGamesForTournament(collection: string, tournamentId: string) {
      if (this.gamesForActiveTournaments.find((data) => data.collection === collection)) {
        return;
      }

      const tournamentGamesData = await this.loadGames({ collection });

      this.gamesForActiveTournaments.push({
        collection,
        tournamentId,
        games: tournamentGamesData.data,
      });
    },
    async getGame({ gameName, provider, id }: IGamesRequestOptions) {
      const urlSearch = new URLSearchParams();

      if (gameName) {
        urlSearch.append('title', gameUrlFromTitle(gameName));
      }

      if (provider) {
        urlSearch.append('provider', provider);
      }

      if (id) {
        urlSearch.append('id', id);
      }

      const game: ISsGame = await bitBack().get({
        url: '/backend/games/single?' + urlSearch.toString(),
      });

      return game;
    },
    getGameCurrency(isRealGameMode: boolean) {
      const storeInGameCurrencyExchange = useStoreInGameCurrencyExchange();

      if (isRealGameMode && useStoreSsPlayer().isPlayerAuthorised) {
        return storeInGameCurrencyExchange.isAvailableCurrencyExchange &&
          storeInGameCurrencyExchange.selectedCurrency
          ? storeInGameCurrencyExchange.selectedCurrency
          : useStoreRoot().userCurrency;
      }

      return 'FUN';
    },
    getGameSrc(game: ISsGame, isRealGameMode: boolean) {
      const storeRoot = useStoreRoot();
      const gameCurrency = this.getGameCurrency(isRealGameMode);

      if (game.real[gameCurrency] && game.real[gameCurrency].id) {
        return `${storeRoot.ssUrl}/${storeRoot.userLang}/games/${game.externalId}/${game.real[gameCurrency].id}`;
      }

      return null;
    },
    getGameId(game: ISsGame) {
      return game.real?.FUN?.id || game.real?.BTC?.id || game.real?.USD?.id;
    },
    async findIfInLiveCollection(gameId: string | undefined) {
      if (!gameId) {
        return false;
      }

      const gameData = await this.loadGames({ ids: [gameId], collection: 'live' });

      if (gameData.data?.length) {
        return !!gameData.data.find((game: ISsGame) => game.externalId === gameId);
      }

      return false;
    },
    findIfRestrictedByCurrency(game: ISsGame, isRealGameMode: boolean) {
      if (!game?.id) {
        return false;
      }

      if (isRealGameMode && useStoreSsPlayer().isPlayerAuthorised) {
        const currency = useStoreRoot().userCurrency;

        return !Object.keys(game?.real).includes(currency);
      }

      return false;
    },
    getAvailableCurrenciesList(game: ISsGame) {
      if (!useStoreSsPlayer().isPlayerAuthorised) {
        return [];
      }

      const balances = useStoreSsAccounts().balanceWithDollarEquivalent;
      const gameCurrencies = Object.keys(game?.real || {});
      const availableCurrenciesList = [] as { currency: string; iconUrl: string }[];

      balances.forEach((balance) => {
        if (gameCurrencies.includes(balance.currency)) {
          let iconUrl: string = '';
          const currencyData = useStoreCurrencyMenu().activeCurrencies.find(
            (c) => c.currencyCode === balance.currency
          );

          if (currencyData) {
            iconUrl = currencyData.currencyIcon?.url;
          }

          availableCurrenciesList.push({ currency: balance.currency, iconUrl });
        }
      });

      return availableCurrenciesList;
    },
    getAvailableCurrenciesWithFreespins(game: ISsGame) {
      if (!useStoreSsPlayer().isPlayerAuthorised) {
        return [];
      }

      const availableCurrenciesList = [] as { currency: string; iconUrl: string }[];
      const gameCurrencies = Object.keys(game?.real);
      const availableFreespins = useStoreSsPlayerBonuses().freespins.filter(
        (fs) => fs.games?.includes(game.externalId) && fs.stage === ESsPlayerFreespinStage.ACTIVATED
      );

      availableFreespins.forEach((fs) => {
        if (gameCurrencies.includes(fs.currency)) {
          let iconUrl: string = '';
          const currencyData = useStoreCurrencyMenu().activeCurrencies.find(
            (c) => c.currencyCode === fs.currency
          );

          if (currencyData) {
            iconUrl = currencyData.currencyIcon?.url;
          }

          availableCurrenciesList.push({ currency: fs.currency, iconUrl });
        }
      });

      return availableCurrenciesList;
    },
    checkAndCreateAvailableFreespinsMessage(game: ISsGame) {
      const availableFreespinsCurrencies = this.getAvailableCurrenciesWithFreespins(game);
      const isUsingAvailableFreespinsCurrency = availableFreespinsCurrencies.find(
        (c) => c.currency === useStoreRoot().userCurrency
      );

      if (availableFreespinsCurrencies.length && !isUsingAvailableFreespinsCurrency) {
        const currenciesList = availableFreespinsCurrencies.map((c) => c.currency).join(', ');

        useStoreToasts().add(ETriggerId.FreespinsAvailable, { postMessage: currenciesList });
      }
    },
    findReplacementCollection() {
      let replacementCollection = '';

      if (useStoreSsPlayer().isPlayerAuthorised) {
        const groupMatch = Object.keys(GAME_SORTING_GROUP_MAP).find((group) =>
          useStoreSsPlayer().playerGroupIds.includes(group)
        );

        if (groupMatch) {
          replacementCollection = GAME_SORTING_GROUP_MAP[groupMatch as 'LOVOL' | 'HIVOL'];
        }
      }

      return replacementCollection;
    },
    addGameToLocalStorage(game: ISsGame) {
      const playerId = useStoreSsPlayer().playerId;

      if (playerId) {
        this.getGamesFromLocalStorage();

        const gamesFromLocalStorage: ISsGame[] = JSON.parse(
          JSON.stringify(this.recentlyPlayedGames)
        );
        const updatedGames = gamesFromLocalStorage.filter((g) => g.id !== game.id);

        if (updatedGames.length >= 30) {
          updatedGames.slice(gamesFromLocalStorage.length - 1);
        }

        updatedGames.unshift(game);
        localStorage.setItem(`${playerId}_last_games`, JSON.stringify(updatedGames));
      }
    },
    getGamesFromLocalStorage() {
      const playerId = useStoreSsPlayer().playerId;
      let gamesFromLocalStorage = [] as ISsGame[];

      if (playerId) {
        const games = localStorage.getItem(`${playerId}_last_games`);

        if (games) {
          gamesFromLocalStorage = (JSON.parse(games) as ISsGame[]).filter((game) => game.id);
        }
      }

      this.recentlyPlayedGames = gamesFromLocalStorage;
    },
  },
});
